// Context
import FiltersContext from '../context/FiltersContext';
import PatientsContext from '../context/PatientsContext';
import ColumnsContext from '../context/ColumnsContext';

// Hooks
import { useCallback, useContext, useMemo } from 'react';
import useSearchbar from './useSearchbar';
import { Patient } from '../interfaces/Patient';
import { getPatientAdmitDays } from '../services/Patient';

const useFilteredPatients = () => {

    const columnsContext = useContext(ColumnsContext);
    const { patientFilters, dispatchPatientsFilters, profiles } = useContext(FiltersContext);
    const { patients } = useContext(PatientsContext);

    const { currentSearchbarText, daysEntryRange, currentProfileID } = patientFilters;

    const getFilterSearchbar =
        useSearchbar(columnsContext.columnsPatients)(currentSearchbarText || '');

    const handleChangeSearchbar = (evt: React.ChangeEvent<HTMLInputElement>) =>
        dispatchPatientsFilters({ type: 'SEARCHBAR', text: evt.target.value });

    const homeProfileIds = useMemo(() => {
        return profiles.map(p => p.id);
    }, [profiles]);

    const isHomePatient = useCallback((patient: Patient) => {
        const profileId = patient.cases?.[0]?.follow_ups?.[0]?.profile?.id;
        if (profileId === undefined || profileId === null) return false;
        return homeProfileIds.includes(profileId);
    }, [homeProfileIds]);

    const filterPatientsByProfile = useCallback(() => (patient: Patient) => {
        if (currentProfileID === null) return true;

        const profileId = patient.cases?.[0]?.follow_ups?.[0]?.profile?.id;
        if (profileId === undefined || profileId === null) return false;

        return profileId === currentProfileID;
    }, [currentProfileID]);

    const filterPatientsEntryDays = useCallback(() => (patient: Patient) => {
        const days = getPatientAdmitDays(patient);
        if (days === null) return false;
        return (days >= daysEntryRange.min) &&
            (daysEntryRange.max === null || days <= daysEntryRange.max);
    }, [daysEntryRange]);

    const filteredPatients = useMemo(() => {
        const filters = [getFilterSearchbar, filterPatientsByProfile, filterPatientsEntryDays];

        return patients.filter(patient => {
            if (!isHomePatient(patient)) return false;

            let filterP = true;
            filters.forEach(filter => {
                if (filter()(patient) === false) filterP = false;
            });

            return filterP;
        });

    }, [patients, getFilterSearchbar, filterPatientsByProfile, filterPatientsEntryDays, isHomePatient]);

    const numFilteredPatients = useMemo(() => (
        `(${filteredPatients.length}/${patients.length})`
    ), [filteredPatients, patients]);

    const filtersActive = useMemo(() => (
        patientFilters.currentProfileID !== null ||
        patientFilters.daysEntryRange.min !== 0 ||
        patientFilters.daysEntryRange.max !== null
    ), [patientFilters.currentProfileID, patientFilters.daysEntryRange.min, patientFilters.daysEntryRange.max]);

    return { filteredPatients, filtersActive, numFilteredPatients, handleChangeSearchbar };
};

export default useFilteredPatients;